import { providerInView } from './providerInView';
import { getPerfomanceTimings, getNavTimings, getProviderLoadTimings } from './performance';
import { loadScript, loadStyle } from './loadAsync';
import { retry } from './retry';
import { getProviderData } from '@indeed/mosaic-provider-utils/dist/web/_internal/setup/providerData';
import { addClassToElement } from '@indeed/mosaic-provider-utils/dist/web/_internal/utils/addClassToElement';
import { getZone, getIndexInZone, isLazy, visibility, getPlatformData, getLoadMethod } from './initializeProvidersUtils';
import { getPlatformLogTk } from './getPlatformLogTk';
import { windowProviderDebug } from './debug';
import { log as logProviderDimensionChange, AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/mosaic/ProviderDimensionChange';
import { SharedDepsClient } from '@indeed/frontend-shared-deps-loader/dist/api/client';
// Keep track of the mutation observers per provider id
// so that we can call disconnect() on the observer
// after we emit height for the provider
export const mutationObservers = {};
// Keep track of providers who have emitted height, if they have, then
// we want to remove the mutation observer from the provider.
export const hasEmittedHeight = {};
// Any height seen (in px) larger than HEIGHT_LOWER_BOUND
// will cause the MutationObserver callback function to
// emit a message to mosaicProviderDimensionChange,
// if the provider hasn't already emitted to that
// table.
export const HEIGHT_LOWER_BOUND = 10;
/*
 * Attempt to execute a Provider JavaScript bundle once it's been loaded.
 * Logging the success or failure of the execution
 **/
const buildMountProvider = ({ id, logger, resolve, reject, jsPath, initializationContext }) => () => {
    var _a;
    try {
        if (window.performance && window.performance.mark) {
            window.performance.mark(`${id}_execution_start`);
        }
        const providerFn = (_a = window.mosaic.providerFNs) === null || _a === void 0 ? void 0 : _a[id];
        if (!providerFn) {
            const msg = `providerFn for ${id} is missing, this probably means that register() was not called`;
            windowProviderDebug(id, msg);
            throw new Error(msg);
        }
        const config = providerFn === null || providerFn === void 0 ? void 0 : providerFn();
        if (window.performance && window.performance.mark) {
            window.performance.mark(`${id}_execution_end`);
        }
        const data = getProviderData(id);
        const seenData = data.seenData;
        const eventData = data.eventData;
        const shouldLogClicks = window.mosaic && window.mosaic.shouldLogProviderContainerClicks
            ? window.mosaic.shouldLogProviderContainerClicks[id]
            : false;
        // Add class to signify the provider has been hydrated
        if (config && config.providerEl) {
            addClassToElement(config.providerEl, 'mosaic-provider-hydrated');
            windowProviderDebug(id, 'Provider hydrated');
            if (shouldLogClicks && !clicksEnabled(id)) {
                document.body.addEventListener('click', (event) => {
                    if (config.providerEl.contains(event.target)) {
                        logger.trackInteraction('mosaicContainerClick', Object.assign(Object.assign(Object.assign({ provider: id, parentLogId: getPlatformLogTk(window.mosaic, id), loadMethod: getLoadMethod(window.mosaic, id) }, visibility(id, true)), getClickTargetInfo(event)), eventData));
                    }
                }, true // uses capture phase in case other listeners stop propagation
                );
                registerClicksEnabled(id);
            }
        }
        if (config &&
            config.providerOptions &&
            config.providerOptions.logInViewport &&
            !seenEnabled(id)) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            providerInView(config.providerEl, (entry) => {
                logger.trackEvent(Object.assign(Object.assign(Object.assign({ logType: 'mosaicProviderSeen', provider: id, parentLogId: getPlatformLogTk(window.mosaic, id), loadMethod: getLoadMethod(window.mosaic, id) }, visibility(id, true, entry.boundingClientRect)), seenData), eventData));
                if (config &&
                    config.providerOptions &&
                    config.providerOptions.onProviderInViewport) {
                    config.providerOptions.onProviderInViewport({ seenData, eventData });
                }
            });
            registerSeenEnabled(id);
        }
        // If the execution fails, we still want to look up the element so we can log the zone and index
        const element = config ? config.providerEl : document.getElementById(id);
        const zone = getZone(element, id);
        logger.trackEvent(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ logType: 'jsuipProviderLoadTimings', provider: id, parentLogId: getPlatformLogTk(window.mosaic, id), isLazy: isLazy(id), zoneId: zone, indexInZone: getIndexInZone(id, zone), initializationType: initializationContext.type, loadMethod: getLoadMethod(window.mosaic, id) }, visibility(id, true)), getNavTimings()), getPerfomanceTimings()), getProviderLoadTimings(id, jsPath)), eventData));
        const loadTimings = getProviderLoadTimings(id, jsPath);
        const vis = visibility(id, true);
        const lazy = isLazy(id);
        const index = getIndexInZone(id, zone);
        windowProviderDebug(id, `Index in zone: ${index}`);
        windowProviderDebug(id, `Lazy: ${lazy}`);
        windowProviderDebug(id, `Load Timing: loadStart[${loadTimings.loadStart}] loadEnd[${loadTimings.loadEnd}] executionStart[${loadTimings.executionStart}] executionEnd[${loadTimings.executionEnd}]`);
        windowProviderDebug(id, `Visibility: Height[${vis.height}] Width[${vis.width}] HasHtml[${vis.hasHeight}]`);
        // Create a MutationObserver that will watch the provider
        // to see if they eventually render any content, but only
        // do so after emitting load timings, so we can see if the provider
        // already has emitted height
        if (config && config.providerEl && hasEmittedHeight[id] !== true) {
            const mutationObserver = new MutationObserver(generateMutationObserverCallback(id));
            mutationObserver.observe(config.providerEl, {
                subtree: true, // observe both the div and all children of the div
                attributes: true, // emit whenever an attribute has changed (like the class name)
                childList: true, // checks for the addition of new child nodes or removal of existing child nodes
                characterData: true // observe for changes to the character data contained within the node or nodes.
            });
            mutationObservers[id] = mutationObserver;
        }
        resolve(config);
        return config;
    }
    catch (e) {
        try {
            const element = document.getElementById(id);
            const zone = getZone(element, id);
            logger.trackError('jsuipProviderExecutionError', e, id, {
                initializationType: initializationContext.type,
                zoneId: zone,
                indexInZone: getIndexInZone(id, zone),
                errorLocation: 'initialization'
            });
            // @ts-ignore
            windowProviderDebug(id, `Caught error during initialization: ${e.toString()}`);
            throw e;
        }
        catch (error) {
            reject(error);
            throw error;
        }
    }
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getClickTargetInfo = (event) => {
    const target = event.target;
    const targetParent = target ? target.parentElement : null;
    return {
        clickTargetId: target ? target.id : null,
        clickTargetTag: target ? target.tagName : null,
        clickTargetClass: target ? target.getAttribute('class') : null,
        clickTargetParentId: targetParent ? targetParent.id : null,
        clickTargetParentTag: targetParent ? targetParent.tagName : null,
        clickTargetParentClass: targetParent ? targetParent.getAttribute('class') : null
    };
};
const buildHandleProviderLoadError = ({ id, logger, reject, initializationContext }) => (e) => {
    logger.trackError('jsuipProviderLoadError', e, id, {
        initializationType: initializationContext.type
    });
    windowProviderDebug(id, 'Provider Load Error: ' + e.name + ':' + e.message);
    reject(e);
    throw e;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const initializeRuntime = (logger) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return retry(() => {
        return (SharedDepsClient.v1
            .load('module', {
            prereqUrls: [],
            urls: [window.mosaic.platform.module.url],
            container: window.mosaic.platform.module.container,
            module: window.mosaic.platform.module.name,
            shareScope: window.mosaic.platform.module.shareScope
        })
            // The shared module loader actually returns the module,
            // but our runtime module is exclusively side-effects,
            // and elsewhere we depend on this being void.
            .then(() => {
            return;
        }));
    })
        .then(({ data, retryCount }) => {
        if (retryCount > 0) {
            logger.trackEvent({
                logType: 'mosaicPlatformLoadRecovery',
                retriesCount: retryCount
            });
        }
        return data;
    })
        .catch((error) => {
        logger.trackError('jsuipPlatformLoadError', error, undefined);
        // TODO: potentially remove SSR'ed HTML in the event where platform deps fail to load
        // Throw an error so that providers trigger a load error, and not an execution error
        throw new Error('Failed to load mosaic common dependencies');
    });
};
const initializeProviders = (providers, translations, logger, existingInitialization) => {
    let mosaicRuntime;
    let onInitialization;
    let initializationContext;
    if (!existingInitialization) {
        mosaicRuntime = initializeRuntime(logger);
        onInitialization = (initialization) => {
            // Execute functions enqueued by host or providers that rely on
            // the Mosaic runtime to be initialized
            if (window.mosaic.apiQueue) {
                window.mosaic.apiQueue.forEach((fn) => {
                    try {
                        fn(initialization);
                    }
                    catch (e) {
                        if (typeof window.reportError === 'function') {
                            window.reportError(e);
                        }
                    }
                });
            }
            // After clearing the queue, execute newly-enqueued functions immediately
            window.mosaic.onMosaicApiReady = (fn) => fn(initialization);
        };
        initializationContext = {
            type: 'initial'
        };
    }
    else {
        mosaicRuntime = existingInitialization;
        onInitialization = () => { };
        initializationContext = {
            type: 'update'
        };
    }
    const platformInitialization = mosaicRuntime
        .then((init) => {
        const initialization = init || {};
        window.mosaic.lazyFns = window.mosaic.lazyFns || {};
        // Relocate emotion styles to the head before provider hydration
        // Reference: https://github.com/emotion-js/emotion/blob/1135f8e9d97ea711eb483368313afdfe7b176845/packages/cache/src/index.js#L56
        const ssrStyles = document.querySelectorAll(`style[data-emotion]:not([data-s])`);
        ssrStyles.forEach((node) => {
            const dataEmotionAttribute = node.getAttribute('data-emotion');
            if (!dataEmotionAttribute || dataEmotionAttribute.indexOf(' ') === -1) {
                return;
            }
            document.head.appendChild(node);
            node.setAttribute('data-s', '');
        });
        Object.keys(providers).forEach((providerId) => {
            initialization[providerId] = new Promise((resolveProviderInitialization, rejectProviderInitialization) => {
                windowProviderDebug(providerId, 'Initializing provider');
                // Loading ${example_provider}'s JavaScript bundle registers
                // an entry function to the global object. `mountProvider`
                // executes that function, and resolves ${example_provider}'s
                // initialization promise if successful
                const mountProvider = buildMountProvider({
                    id: providerId,
                    logger,
                    resolve: resolveProviderInitialization,
                    reject: rejectProviderInitialization,
                    jsPath: providers[providerId],
                    initializationContext
                });
                // Performs logging and cleanup if a provider's JavaScript bundle
                // fails to load or throws an error while executing
                const handleLoadError = buildHandleProviderLoadError({
                    id: providerId,
                    logger,
                    reject: rejectProviderInitialization,
                    initializationContext
                });
                // Begins loading a provider's static assets. When its JavaScript
                // bundle is loaded and executed, the provider's entry function is
                // registered to the global object
                const registerProvider = () => {
                    if (!registerProvider._registration) {
                        const translationAssetPromise = translations && translations[providerId]
                            ? retry(() => loadScript(translations[providerId]))
                            : Promise.resolve();
                        // Currently, CSS is only dynamically loaded for providers that are
                        // discovered and initialized after initial page load. Lazy providers
                        // that are known at initial page load time eagerly load their CSS asset
                        const stylePath = window.mosaic.providerCss &&
                            window.mosaic.providerCss[providerId];
                        const styleAssetPromise = stylePath
                            ? retry(() => loadStyle(stylePath))
                            : Promise.resolve();
                        const javaScriptAssetPromise = retry(() => loadScript(providers[providerId]));
                        registerProvider._registration = Promise.all([
                            mosaicRuntime,
                            javaScriptAssetPromise,
                            styleAssetPromise,
                            translationAssetPromise
                        ]).then(() => ({ mount: mountProvider }), handleLoadError);
                    }
                    return registerProvider._registration;
                };
                // Loads a provider's static assets and mounts the provider after
                // it has been registered
                const initProvider = () => {
                    return registerProvider().then(({ mount }) => mount());
                };
                // Expose an API to register the provider without immediately
                // mounting it
                initProvider.register = registerProvider;
                // if a provider is declare "lazy", we dont immediately execute it
                // instead, we let hosts, trigger when the Provider should be loaded, etc
                if (isLazy(providerId)) {
                    window.mosaic.lazyFns[providerId] = initProvider;
                }
                else {
                    // otherwise we execute immediately
                    initProvider().catch(() => { });
                }
            });
            initialization[providerId].catch(() => {
                windowProviderDebug(providerId, 'Caught error while initializing');
            });
        });
        onInitialization(initialization);
        return initialization;
    })
        .catch((e) => ({}));
    return platformInitialization;
};
// Checks to see if we have previously added a click event watcher
// to the provider in question
const clicksEnabled = (providerId) => {
    if (!window.mosaic) {
        return false;
    }
    if (!window.mosaic.providerEventsEnabled) {
        window.mosaic.providerEventsEnabled = {
            seen: {},
            clicks: {}
        };
    }
    window.mosaic.providerEventsEnabled.clicks = window.mosaic.providerEventsEnabled.clicks || {};
    return window.mosaic.providerEventsEnabled.clicks[providerId] === true;
};
// Checks to see if we have previously added an in view watcher
// to the provider in question
const seenEnabled = (providerId) => {
    if (!window.mosaic) {
        return false;
    }
    if (!window.mosaic.providerEventsEnabled) {
        window.mosaic.providerEventsEnabled = {
            seen: {},
            clicks: {}
        };
    }
    window.mosaic.providerEventsEnabled.seen = window.mosaic.providerEventsEnabled.seen || {};
    return window.mosaic.providerEventsEnabled.seen[providerId] === true;
};
// Registers that we have enabled a click event watcher
// on the provider so we don't do it multiple times
const registerClicksEnabled = (providerId) => {
    if (!window.mosaic) {
        return;
    }
    window.mosaic.providerEventsEnabled.clicks = window.mosaic.providerEventsEnabled.clicks || {};
    window.mosaic.providerEventsEnabled.clicks[providerId] = true;
};
// Registers that we have enabled an in view watcher
// on the provider so we don't do it multiple times
const registerSeenEnabled = (providerId) => {
    if (!window.mosaic) {
        return;
    }
    window.mosaic.providerEventsEnabled.seen = window.mosaic.providerEventsEnabled.seen || {};
    window.mosaic.providerEventsEnabled.seen[providerId] = true;
};
// Whenever a mutation is observed for a provider,
// we want to emit visibility data for that provider
// if it's the first time we've seen a height
// larger than HEIGHT_LOWER_BOUND.
// This is a higher order function because we need to be
// able to access the providerId when writing to signals
// and we aren't guaranteed to be able to get the provider
// id from the MutationRecord.
export const generateMutationObserverCallback = (providerId) => {
    return (entries) => {
        entries.forEach(() => {
            const id = providerId;
            // First check to see if we've already
            // emitted height for this provider,
            // if we have then we want to
            // remove the mutation observer
            if (hasEmittedHeight[providerId]) {
                mutationObservers[providerId].disconnect();
                return;
            }
            // We only want to emit the first time that we see height on the provider
            // and we want the height to be larger than 10px at least.
            const visibilityData = visibility(id);
            if (visibilityData.height > HEIGHT_LOWER_BOUND) {
                const platformData = getPlatformData(window.mosaic);
                // This needs to follow the schema for the
                // mosaicProviderDimensionChange LogRepo table,
                // found here:
                // https://indeed.sourcegraph.com/code.corp.indeed.com/frontend/signals/-/blob/schemas/src/main/avro/signals/mosaic/MosaicEvents.avdl#L4
                logProviderDimensionChange({
                    providerId: id,
                    hostId: platformData.host,
                    parentLogId: AvroOptional.string(getPlatformLogTk(window.mosaic, id)),
                    pageId: AvroOptional.string(platformData.pageId),
                    height: AvroOptional.double(visibilityData.height),
                    width: AvroOptional.double(visibilityData.width),
                    hasHtml: AvroOptional.boolean(visibilityData.hasHtml)
                });
                // disconnect the mutation observer after we emit that
                // we've seen height
                mutationObservers[id].disconnect();
                hasEmittedHeight[id] = true;
            }
        });
    };
};
export default initializeProviders;
